// client/src/hooks/support/useSupportPreferences.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from '../../lib/firebase/authReadyCallable';
import { db, functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import type { SupportPreferences, InboxFilters } from '../../types/support/inbox';
import {
  DEFAULT_SUPPORT_PREFERENCES,
  supportPreferencesKeys,
} from '../../types/support/inbox';

/**
 * Read/write support preferences for the current user.
 * Stored at users/{uid}/preferences/support in Firestore.
 */
export function useSupportPreferences() {
  const { user } = useAuth();
  const userId = user?.userId ?? '';

  return useQuery({
    queryKey: supportPreferencesKeys.user(userId),
    queryFn: async (): Promise<SupportPreferences> => {
      const docRef = doc(db, 'users', userId, 'preferences', 'support');
      const snap = await getDoc(docRef);
      if (!snap.exists()) return DEFAULT_SUPPORT_PREFERENCES;
      return { ...DEFAULT_SUPPORT_PREFERENCES, ...snap.data() } as SupportPreferences;
    },
    enabled: !!userId,
  });
}

/** What the server actually stored (the sanitizer may drop keys it refuses). */
export interface UpdateSupportPreferencesResult {
  success: boolean;
  inboxDefaults?: Partial<InboxFilters>;
}

export function useUpdateSupportPreferences() {
  const { user } = useAuth();
  const userId = user?.userId ?? '';
  const { currentOrganization } = useOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (prefs: Partial<SupportPreferences>): Promise<UpdateSupportPreferencesResult> => {
      const callable = httpsCallable<unknown, UpdateSupportPreferencesResult>(functions, 'support_dispatch');
      const res = await callable({
        action: 'updatePreferences',
        organizationId: currentOrganization?.organizationId || '',
        preferences: prefs,
      });
      return res.data;
    },
    onMutate: async (prefs) => {
      const key = supportPreferencesKeys.user(userId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<SupportPreferences>(key);
      queryClient.setQueryData(key, (old: SupportPreferences | undefined) => ({
        ...(old ?? DEFAULT_SUPPORT_PREFERENCES),
        ...prefs,
      }));
      return { previous };
    },
    // The server's sanitizer may drop keys without failing the call, so make the
    // cache equal what it STORED (the echo), not what we sent. Runs before the
    // caller's own onSuccess (toast).
    onSuccess: (result) => {
      if (!result?.inboxDefaults) return;
      queryClient.setQueryData(
        supportPreferencesKeys.user(userId),
        (old: SupportPreferences | undefined) => ({
          ...(old ?? DEFAULT_SUPPORT_PREFERENCES),
          inboxDefaults: result.inboxDefaults as InboxFilters,
        }),
      );
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          supportPreferencesKeys.user(userId),
          context.previous,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: supportPreferencesKeys.user(userId) });
    },
  });
}
